import { X } from "lucide-react";
import { clsx } from "clsx";
import { Dialog } from "radix-ui";
import React, { PropsWithChildren } from "react";
import { twMerge } from "tailwind-merge";

export type DrawerAccent = "none" | "critical" | "warning" | "success" | "info";

const accentBarClasses: Record<Exclude<DrawerAccent, "none">, string> = {
  critical: "bg-borderError",
  warning: "bg-yellow-500",
  success: "bg-green-700",
  info: "bg-blue-500",
};

interface DrawerProps {
  title: string;
  eyebrow?: React.ReactNode;
  subtitle?: React.ReactNode;
  accent?: DrawerAccent;
  accentColor?: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  children: React.ReactNode;
  placement: "bottom" | "right";
  className?: string;
  titleClassName?: string;
  overlayProps?: Partial<Dialog.DialogOverlayProps>;
}

export function Drawer({
  title,
  eyebrow,
  subtitle,
  accent = "none",
  accentColor,
  children,
  onOpenChange,
  placement,
  className,
  titleClassName,
  overlayProps,
  ...rest
}: DrawerProps) {
  return (
    <Dialog.Root {...rest}>
      <Dialog.Portal>
        <Dialog.Overlay
          {...overlayProps}
          className={twMerge(
            clsx(
              "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut z-overlay fixed top-0 left-0 flex h-full w-full flex-col bg-black/50 pt-12",
              overlayProps?.className,
            ),
          )}
        >
          <Dialog.Content
            onEscapeKeyDown={() => onOpenChange(false)}
            onPointerDownOutside={() => onOpenChange(false)}
            className={clsx(
              "flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-gray-100",
              placement === "bottom" &&
                "data-[state=open]:animate-slideInUp data-[state=closed]:animate-slideOutDown rounded-t-md",
              placement === "right" &&
                "data-[state=open]:animate-slideInFromRight data-[state=closed]:animate-slideOutToRight fixed top-0 right-0 h-full w-96 max-w-[90vw]",
              className,
            )}
          >
            <div
              className={clsx(
                "border-surface-interactive flex flex-0 justify-between gap-4 border-b px-8 py-6",
                eyebrow || subtitle ? "items-start" : "items-center",
                titleClassName,
              )}
            >
              <div className="flex min-w-0 flex-1 items-stretch gap-4">
                {(accent !== "none" || accentColor) && (
                  <div
                    className={clsx(
                      "w-1 shrink-0 self-stretch",
                      !accentColor &&
                        accent !== "none" &&
                        accentBarClasses[accent],
                    )}
                    style={
                      accentColor ? { backgroundColor: accentColor } : undefined
                    }
                    aria-hidden="true"
                  />
                )}
                <div className="flex min-w-0 flex-col gap-1">
                  {eyebrow && (
                    <span className="text-textSubtle truncate text-xs">
                      {eyebrow}
                    </span>
                  )}
                  <Dialog.Title className="text-textDefault text-2xl font-bold">
                    {title}
                  </Dialog.Title>
                  {subtitle && (
                    <span className="text-textSubtle truncate text-sm">
                      {subtitle}
                    </span>
                  )}
                </div>
              </div>
              <Dialog.Close
                onClick={() => onOpenChange(false)}
                className="text-textSubtle hover:text-textDefault shrink-0 cursor-pointer transition-colors"
              >
                <X />
              </Dialog.Close>
            </div>
            {children}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function DrawerContent({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={twMerge(
        clsx("min-h-0 flex-1 overflow-auto px-8 py-10", className),
      )}
    >
      {children}
    </div>
  );
}

export function DrawerCtaSection({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={twMerge(
        clsx("border-surface-interactive flex-0 border-t px-8 py-4", className),
      )}
    >
      {children}
    </div>
  );
}
